/**
 * Visit Completed Trigger Component
 *
 * Trigger: "checked in/completed a visit"
 * Shows: service select + count input
 */

import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchServicesOptions } from 'src/models/service/service';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import { TriggerComponentProps } from '../automationTriggers';

export const VisitCompletedTrigger: FC<TriggerComponentProps> = ({
  automation,
  index,
  readOnly,
}) => {
  const { t } = useTranslation();
  const { data: services = [] } = useQuery({
    queryKey: ['services', 'options'],
    queryFn: () => fetchServicesOptions(),
    staleTime: 60000,
  });

  const serviceOptions = services.map((service) => ({
    value: String(service.id),
    label: service.name,
  }));

  const selectedService = services.find(
    (s) => String(s.id) === String(automation.reminderTypeId)
  );
  const count = automation.count || 1;

  if (readOnly) {
    const serviceName = selectedService?.name || t('automations.triggers.anyServiceCap');
    return (
      <span>
        {t('automations.triggers.checkedInVisitCount', { count, service: serviceName })}
      </span>
    );
  }

  // Edit mode
  return (
    <Stack spacing={2}>
      <Box sx={{ maxWidth: 300 }}>
        <FormSelect
          name={`automations.${index}.reminderTypeId`}
          label={t('automations.triggerLabels.service')}
          options={serviceOptions}
          prompt={t('automations.triggers.anyServiceCap')}
        />
      </Box>
      <Box sx={{ maxWidth: 150 }}>
        <FormTextField
          name={`automations.${index}.count`}
          label={t('automations.triggerLabels.count')}
          placeholder={t('automations.triggers.visitCountPlaceholder')}
          type="number"
          InputProps={{ inputProps: { min: 1 } }}
        />
      </Box>
    </Stack>
  );
};
